import React, { useState, useEffect } from "react";
import {
  Shield,
  Users,
  Settings,
  Activity,
  Key,
  Database,
  Zap,
  Clock,
  Search,
  Filter,
  RefreshCw,
  Download,
  Eye,
  Edit,
  UserPlus,
  BarChart3,
  CheckCircle,
  XCircle,
  AlertCircle,
  Lock,
  Unlock,
  RotateCw,
  Trash2,
  Building2,
  FileText,
  Mail,
  Phone,
  Calendar,
  Target,
  TrendingUp,
  ChevronRight,
  X,
  UserCircle,
  ScrollText,
} from "lucide-react";
import toast from "react-hot-toast";
import UserManagement from "./UserManagement";
import Integrations from "./Integrations";
import Configuration from "./Configuration";
import IntegratedFlowView from "../components/IntegratedFlowView";
import CandidateJourneyFlow from "../components/CandidateJourneyFlow";
import AuditLogViewer from "../components/shared/AuditLogViewer";
import ContactFormModal from "../components/ContactFormModal";
import ContactsTab from "../components/ContactsTab";
import CompanyCreateForm from "../components/CompanyCreateForm";
import CompaniesTab from "../components/CompaniesTab";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

// Persona/Role types
const PERSONA_ROLES = [
  { value: "junoadmin", label: "Juno Admin", color: "purple" },
  { value: "admin", label: "Admin", color: "red" },
  { value: "recruiter", label: "Recruiter", color: "green" },
  { value: "lead", label: "Lead", color: "blue" },
  { value: "sales", label: "Sales", color: "orange" },
  { value: "hr", label: "HR", color: "indigo" },
];

const AdminAndrewDashboard = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showAddOperationModal, setShowAddOperationModal] = useState(false);
  const [selectedPersonaForOperation, setSelectedPersonaForOperation] =
    useState(null);
  const [selectedRole, setSelectedRole] = useState("admin"); // For role selector in Role Management

  // Data states
  const [users, setUsers] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [teams, setTeams] = useState([]);
  const [leadRecruiters, setLeadRecruiters] = useState([]);
  const [allRecruiters, setAllRecruiters] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);

  // Contacts state
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactsRefreshTrigger, setContactsRefreshTrigger] = useState(0);

  // Companies state
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companiesRefreshTrigger, setCompaniesRefreshTrigger] = useState(0);

  // Permissions data - operations per persona
  const [rolePermissions, setRolePermissions] = useState({
    admin: [],
    recruiter: [],
    lead: [],
    sales: [],
    hr: [],
    lead_sales_manager: [],
  });

  // Filter states
  const [filters, setFilters] = useState({
    role: "all",
    status: "all",
    supervisor: "all",
    searchTerm: "",
  });

  // Metrics
  const [adminMetrics, setAdminMetrics] = useState({
    totalUsers: 0,
    activeUsers: 0,
    inactiveUsers: 0,
    totalRecruiters: 0,
    totalSales: 0,
    totalLeads: 0,
    activeIntegrations: 0,
    pendingActions: 0,
  });

  useEffect(() => {
    fetchAllData();
    loadDefaultPermissions();
  }, []);

  // Clear search terms when switching tabs
  useEffect(() => {
    setFilters((prev) => ({ ...prev, searchTerm: "" }));
  }, [activeTab]);

  const loadDefaultPermissions = () => {
    // Default operations for each role
    const defaultPermissions = {
      admin: [
        {
          id: "manage_users",
          name: "Manage Users",
          description: "Create, edit, and delete user accounts",
          enabled: true,
          category: "User Management",
        },
        {
          id: "view_all_users",
          name: "View All Users",
          description: "Access all user profiles and data",
          enabled: true,
          category: "User Management",
        },
        {
          id: "reset_passwords",
          name: "Reset Passwords",
          description: "Reset user passwords",
          enabled: true,
          category: "User Management",
        },
        {
          id: "assign_roles",
          name: "Assign Roles",
          description: "Assign and modify user roles",
          enabled: true,
          category: "User Management",
        },
        {
          id: "manage_permissions",
          name: "Manage Permissions",
          description: "Configure role-based permissions",
          enabled: true,
          category: "Role Management",
        },
        {
          id: "create_operations",
          name: "Create Operations",
          description: "Define new operations for roles",
          enabled: true,
          category: "Role Management",
        },
        {
          id: "delete_operations",
          name: "Delete Operations",
          description: "Remove operations from roles",
          enabled: true,
          category: "Role Management",
        },
        {
          id: "view_all_candidates",
          name: "View All Candidates",
          description: "Access all candidate profiles",
          enabled: true,
          category: "Data Access",
        },
        {
          id: "view_all_jobs",
          name: "View All Jobs",
          description: "Access all job requisitions",
          enabled: true,
          category: "Data Access",
        },
        {
          id: "view_all_submissions",
          name: "View All Submissions",
          description: "Access all candidate submissions",
          enabled: true,
          category: "Data Access",
        },
        {
          id: "view_all_customers",
          name: "View All Customers",
          description: "Access all customer data",
          enabled: true,
          category: "Data Access",
        },
        {
          id: "manage_integrations",
          name: "Manage Integrations",
          description: "Configure third-party integrations",
          enabled: true,
          category: "System Configuration",
        },
        {
          id: "system_settings",
          name: "System Settings",
          description: "Modify system-wide settings",
          enabled: true,
          category: "System Configuration",
        },
        {
          id: "view_audit_logs",
          name: "View Audit Logs",
          description: "Access system activity logs",
          enabled: true,
          category: "System Configuration",
        },
        {
          id: "backup_restore",
          name: "Backup & Restore",
          description: "Perform system backup and restore",
          enabled: true,
          category: "System Configuration",
        },
        {
          id: "export_all_data",
          name: "Export All Data",
          description: "Export complete system data",
          enabled: true,
          category: "Reporting",
        },
        {
          id: "view_analytics",
          name: "View Analytics",
          description: "Access all analytics and reports",
          enabled: true,
          category: "Reporting",
        },
        {
          id: "manage_announcements",
          name: "Manage Announcements",
          description: "Create system-wide announcements",
          enabled: true,
          category: "Communication",
        },
      ],
      recruiter: [
        {
          id: "view_candidates",
          name: "View Candidates",
          description: "View all candidate profiles",
          enabled: true,
          category: "Candidate Management",
        },
        {
          id: "create_candidates",
          name: "Create Candidates",
          description: "Add new candidate profiles",
          enabled: true,
          category: "Candidate Management",
        },
        {
          id: "edit_candidates",
          name: "Edit Candidates",
          description:
            "Modify candidate information (only if assigned as primary recruiter)",
          enabled: true,
          category: "Candidate Management",
        },
        {
          id: "delete_candidates",
          name: "Delete Candidates",
          description: "Remove candidate profiles",
          enabled: false,
          category: "Candidate Management",
        },
        {
          id: "assign_primary_recruiter",
          name: "Assign Primary Recruiter",
          description: "Set primary recruiter for candidates",
          enabled: true,
          category: "Candidate Management",
        },
        {
          id: "view_jobs",
          name: "View Jobs",
          description: "View all job requisitions",
          enabled: true,
          category: "Job Management",
        },
        {
          id: "create_jobs",
          name: "Create Jobs",
          description: "Create new job requisitions",
          enabled: false,
          category: "Job Management",
        },
        {
          id: "edit_jobs",
          name: "Edit Jobs",
          description: "Modify job requisitions",
          enabled: false,
          category: "Job Management",
        },
        {
          id: "submit_candidates",
          name: "Submit Candidates",
          description: "Submit candidates to job requisitions",
          enabled: true,
          category: "Submission Management",
        },
        {
          id: "view_submissions",
          name: "View Submissions",
          description: "View candidate submissions",
          enabled: true,
          category: "Submission Management",
        },
        {
          id: "withdraw_submissions",
          name: "Withdraw Submissions",
          description: "Withdraw candidate submissions",
          enabled: true,
          category: "Submission Management",
        },
        {
          id: "view_customers",
          name: "View Customers",
          description: "View customer/client information",
          enabled: false,
          category: "Customer Management",
        },
        {
          id: "view_analytics",
          name: "View Analytics",
          description: "Access analytics and reports",
          enabled: true,
          category: "Reporting",
        },
        {
          id: "export_data",
          name: "Export Candidate Lists",
          description: "Export candidate data to CSV/Excel",
          enabled: true,
          category: "Reporting",
        },
      ],
      lead: [
        {
          id: "view_candidates",
          name: "View Candidates",
          description: "View all candidate profiles",
          enabled: true,
          category: "Candidate Management",
        },
        {
          id: "create_candidates",
          name: "Create Candidates",
          description: "Add new candidate profiles",
          enabled: true,
          category: "Candidate Management",
        },
        {
          id: "edit_candidates",
          name: "Edit Candidates",
          description: "Modify candidate information",
          enabled: true,
          category: "Candidate Management",
        },
        {
          id: "delete_candidates",
          name: "Delete Candidates",
          description: "Remove candidate profiles",
          enabled: true,
          category: "Candidate Management",
        },
        {
          id: "assign_primary_recruiter",
          name: "Assign Primary Recruiter",
          description: "Set primary recruiter for candidates",
          enabled: true,
          category: "Candidate Management",
        },
        {
          id: "view_jobs",
          name: "View Jobs",
          description: "View all job requisitions",
          enabled: true,
          category: "Job Management",
        },
        {
          id: "create_jobs",
          name: "Create Jobs",
          description: "Create new job requisitions",
          enabled: true,
          category: "Job Management",
        },
        {
          id: "edit_jobs",
          name: "Edit Jobs",
          description: "Modify job requisitions",
          enabled: true,
          category: "Job Management",
        },
        {
          id: "submit_candidates",
          name: "Submit Candidates",
          description: "Submit candidates to job requisitions",
          enabled: true,
          category: "Submission Management",
        },
        {
          id: "view_submissions",
          name: "View Submissions",
          description: "View all candidate submissions",
          enabled: true,
          category: "Submission Management",
        },
        {
          id: "manage_team",
          name: "Manage Team",
          description: "Manage recruiter team members",
          enabled: true,
          category: "Team Management",
        },
        {
          id: "view_customers",
          name: "View Customers",
          description: "View customer/client information",
          enabled: true,
          category: "Customer Management",
        },
        {
          id: "view_analytics",
          name: "View Analytics",
          description: "Access analytics and reports",
          enabled: true,
          category: "Reporting",
        },
        {
          id: "export_data",
          name: "Export Data",
          description: "Export data to CSV/Excel",
          enabled: true,
          category: "Reporting",
        },
      ],
      sales: [
        {
          id: "view_customers",
          name: "View Customers",
          description: "View customer/client information",
          enabled: true,
          category: "Customer Management",
        },
        {
          id: "create_customers",
          name: "Create Customers",
          description: "Add new customer records",
          enabled: true,
          category: "Customer Management",
        },
        {
          id: "edit_customers",
          name: "Edit Customers",
          description: "Modify customer information",
          enabled: true,
          category: "Customer Management",
        },
        {
          id: "view_jobs",
          name: "View Jobs",
          description: "View all job requisitions",
          enabled: true,
          category: "Job Management",
        },
        {
          id: "create_jobs",
          name: "Create Jobs",
          description: "Create new job requisitions",
          enabled: true,
          category: "Job Management",
        },
        {
          id: "edit_jobs",
          name: "Edit Jobs",
          description: "Modify job requisitions",
          enabled: true,
          category: "Job Management",
        },
        {
          id: "submit_candidates",
          name: "Submit Candidates",
          description: "Submit candidates to jobs",
          enabled: false,
          category: "Submission Management",
        },
        {
          id: "view_submissions",
          name: "View Submissions",
          description: "View submission pipeline",
          enabled: true,
          category: "Submission Management",
        },
        {
          id: "view_analytics",
          name: "View Analytics",
          description: "Access sales analytics",
          enabled: true,
          category: "Reporting",
        },
        {
          id: "export_data",
          name: "Export Data",
          description: "Export reports",
          enabled: true,
          category: "Reporting",
        },
      ],
      hr: [
        {
          id: "view_candidates",
          name: "View Candidates",
          description: "View accepted candidates",
          enabled: true,
          category: "Onboarding",
        },
        {
          id: "view_submissions",
          name: "View Submissions",
          description: "View offer accepted submissions",
          enabled: true,
          category: "Onboarding",
        },
        {
          id: "manage_onboarding",
          name: "Manage Onboarding",
          description: "Manage candidate onboarding process",
          enabled: true,
          category: "Onboarding",
        },
        {
          id: "view_customers",
          name: "View Customers",
          description: "View customer information",
          enabled: true,
          category: "Customer Management",
        },
        {
          id: "view_analytics",
          name: "View Analytics",
          description: "Access HR analytics",
          enabled: true,
          category: "Reporting",
        },
        {
          id: "export_data",
          name: "Export Data",
          description: "Export onboarding reports",
          enabled: true,
          category: "Reporting",
        },
      ],
      lead_sales_manager: [
        {
          id: "view_all_customers",
          name: "View All Customers",
          description: "Access all customer/client information",
          enabled: true,
          category: "Customer Management",
        },
        {
          id: "create_customers",
          name: "Create Customers",
          description: "Add new customer records",
          enabled: true,
          category: "Customer Management",
        },
        {
          id: "edit_customers",
          name: "Edit Customers",
          description: "Modify customer information",
          enabled: true,
          category: "Customer Management",
        },
        {
          id: "delete_customers",
          name: "Delete Customers",
          description: "Remove customer records",
          enabled: true,
          category: "Customer Management",
        },
        {
          id: "view_all_jobs",
          name: "View All Jobs",
          description: "Access all job requisitions",
          enabled: true,
          category: "Job Management",
        },
        {
          id: "create_jobs",
          name: "Create Jobs",
          description: "Create new job requisitions",
          enabled: true,
          category: "Job Management",
        },
        {
          id: "edit_jobs",
          name: "Edit Jobs",
          description: "Modify job requisitions",
          enabled: true,
          category: "Job Management",
        },
        {
          id: "delete_jobs",
          name: "Delete Jobs",
          description: "Remove job requisitions",
          enabled: true,
          category: "Job Management",
        },
        {
          id: "view_all_candidates",
          name: "View All Candidates",
          description: "Access all candidate profiles",
          enabled: true,
          category: "Candidate Management",
        },
        {
          id: "assign_recruiters",
          name: "Assign Primary Recruiters",
          description: "Assign/reassign primary recruiters to candidates",
          enabled: true,
          category: "Candidate Management",
        },
        {
          id: "view_all_submissions",
          name: "View All Submissions",
          description: "Access all candidate submissions",
          enabled: true,
          category: "Submission Management",
        },
        {
          id: "view_team_performance",
          name: "View Team Performance",
          description: "Access sales team performance metrics",
          enabled: true,
          category: "Team Management",
        },
        {
          id: "view_sales_analytics",
          name: "View Sales Analytics",
          description: "Access sales pipeline and revenue analytics",
          enabled: true,
          category: "Reporting",
        },
        {
          id: "view_client_performance",
          name: "View Client Performance",
          description: "Track individual client metrics and performance",
          enabled: true,
          category: "Reporting",
        },
        {
          id: "export_reports",
          name: "Export Reports",
          description: "Export sales and performance reports",
          enabled: true,
          category: "Reporting",
        },
        {
          id: "manage_sales_team",
          name: "Manage Sales Team",
          description: "Oversee sales representatives",
          enabled: true,
          category: "Team Management",
        },
      ],
    };

    // Load from localStorage if exists, otherwise use defaults
    const stored = localStorage.getItem("rolePermissions");
    if (stored) {
      try {
        setRolePermissions(JSON.parse(stored));
      } catch (e) {
        setRolePermissions(defaultPermissions);
        localStorage.setItem(
          "rolePermissions",
          JSON.stringify(defaultPermissions)
        );
      }
    } else {
      setRolePermissions(defaultPermissions);
      localStorage.setItem(
        "rolePermissions",
        JSON.stringify(defaultPermissions)
      );
    }
  };

  useEffect(() => {
    calculateMetrics();
  }, [users]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchUsers(),
        fetchPersonas(),
        fetchIntegrations(),
        fetchTeamData(),
      ]);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/users`);
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchPersonas = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/personas`);
      if (response.ok) {
        const data = await response.json();
        setPersonas(data.personas || []);
      }
    } catch (error) {
      console.error("Error fetching personas:", error);
    }
  };

  const fetchIntegrations = async () => {
    // Mock integrations data
    setIntegrations([
      {
        id: 1,
        name: "Microsoft Entra ID",
        type: "Authentication",
        status: "configured",
        lastSync: new Date(),
      },
      {
        id: 2,
        name: "Email Service",
        type: "Communication",
        status: "pending",
        lastSync: null,
      },
      {
        id: 3,
        name: "Calendar Integration",
        type: "Scheduling",
        status: "active",
        lastSync: new Date(),
      },
    ]);
  };

  const calculateMetrics = () => {
    const totalUsers = users.length;
    const activeUsers = users.filter((u) => u.is_active).length;
    const inactiveUsers = totalUsers - activeUsers;
    const totalRecruiters = users.filter((u) => u.role === "recruiter").length;
    const totalSales = users.filter((u) => u.role === "sales").length;
    const totalLeads = users.filter((u) => u.role === "lead").length;
    const activeIntegrations = integrations.filter(
      (i) => i.status === "active"
    ).length;

    setAdminMetrics({
      totalUsers,
      activeUsers,
      inactiveUsers,
      totalRecruiters,
      totalSales,
      totalLeads,
      activeIntegrations,
      pendingActions: 0,
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const fetchTeamData = async () => {
    try {
      // Fetch lead recruiters
      const leadsResponse = await fetch(`${BACKEND_URL}/api/users/leads`);
      if (leadsResponse.ok) {
        const leadsData = await leadsResponse.json();
        setLeadRecruiters(leadsData.leads || []);
      }

      // Fetch all teams
      const teamsResponse = await fetch(`${BACKEND_URL}/api/teams`);
      if (teamsResponse.ok) {
        const teamsData = await teamsResponse.json();
        setTeams(teamsData.teams || []);
      }

      // Fetch all recruiters (role="recruiter")
      const usersResponse = await fetch(`${BACKEND_URL}/api/users`);
      if (usersResponse.ok) {
        const usersData = await usersResponse.json();
        const recruiters = (usersData.users || []).filter(
          (u) => u.role === "recruiter"
        );
        setAllRecruiters(recruiters);
      }
    } catch (error) {
      console.error("Error fetching team data:", error);
      toast.error("Failed to load team data");
    }
  };

  const handleContactCreated = () => {
    setShowContactModal(false);
    // Trigger refresh by updating the key
    setContactsRefreshTrigger((prev) => prev + 1);
  };

  const handleCompanyCreated = () => {
    setShowCompanyModal(false);
    setSelectedCompany(null);
    // Trigger refresh by updating the key
    setCompaniesRefreshTrigger((prev) => prev + 1);
  };

  const handleAddTeamMember = async (teamId, memberId) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/teams/${teamId}/members`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "add",
            member_id: memberId,
          }),
        }
      );

      if (response.ok) {
        toast.success("Team member added successfully");
        fetchTeamData();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to add team member");
      }
    } catch (error) {
      console.error("Error adding team member:", error);
      toast.error("Failed to add team member");
    }
  };

  const handleRemoveTeamMember = async (teamId, memberId) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/teams/${teamId}/members`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "remove",
            member_id: memberId,
          }),
        }
      );

      if (response.ok) {
        toast.success("Team member removed successfully");
        fetchTeamData();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to remove team member");
      }
    } catch (error) {
      console.error("Error removing team member:", error);
      toast.error("Failed to remove team member");
    }
  };

  const getRoleColor = (role) => {
    const roleConfig = PERSONA_ROLES.find((r) => r.value === role);
    return roleConfig ? roleConfig.color : "gray";
  };

  const getRoleBadgeClass = (role) => {
    const colors = {
      junoadmin: "bg-purple-100 text-purple-800",
      admin: "bg-red-100 text-red-800",
      recruiter: "bg-green-100 text-green-800",
      lead: "bg-blue-100 text-blue-800",
      sales: "bg-orange-100 text-orange-800",
      hr: "bg-indigo-100 text-indigo-800",
    };
    return colors[role] || "bg-gray-100 text-gray-800";
  };

  const togglePermission = (role, operationId) => {
    setRolePermissions((prev) => {
      const updated = {
        ...prev,
        [role]: prev[role].map((op) =>
          op.id === operationId ? { ...op, enabled: !op.enabled } : op
        ),
      };
      localStorage.setItem("rolePermissions", JSON.stringify(updated));
      toast.success(
        `Permission ${
          updated[role].find((op) => op.id === operationId).enabled
            ? "enabled"
            : "disabled"
        }`
      );
      return updated;
    });
  };

  const addOperation = (role, newOperation) => {
    setRolePermissions((prev) => {
      const updated = {
        ...prev,
        [role]: [
          ...prev[role],
          { ...newOperation, id: `custom_${Date.now()}`, enabled: true },
        ],
      };
      localStorage.setItem("rolePermissions", JSON.stringify(updated));
      toast.success("Operation added successfully");
      return updated;
    });
  };

  const deleteOperation = (role, operationId) => {
    if (!operationId.startsWith("custom_")) {
      toast.error("Cannot delete default operations");
      return;
    }
    setRolePermissions((prev) => {
      const updated = {
        ...prev,
        [role]: prev[role].filter((op) => op.id !== operationId),
      };
      localStorage.setItem("rolePermissions", JSON.stringify(updated));
      toast.success("Operation deleted successfully");
      return updated;
    });
  };

  // Overview Tab
  const OverviewTab = () => (
    <div className="space-y-6">
      {/* System Health Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Users"
          value={adminMetrics.totalUsers}
          icon={Users}
          color="blue"
          subtitle={`${adminMetrics.activeUsers} active`}
        />
        <MetricCard
          title="Recruiters"
          value={adminMetrics.totalRecruiters}
          icon={Users}
          color="green"
          subtitle="team members"
        />
        <MetricCard
          title="Sales Team"
          value={adminMetrics.totalSales}
          icon={TrendingUp}
          color="orange"
          subtitle="account managers"
        />
        <MetricCard
          title="Team Leads"
          value={adminMetrics.totalLeads}
          icon={Shield}
          color="purple"
          subtitle="supervisors"
        />
      </div>

      {/* System Status */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard
          title="Active Integrations"
          value={adminMetrics.activeIntegrations}
          icon={Zap}
          color="teal"
          subtitle="connected services"
        />
        <MetricCard
          title="Inactive Users"
          value={adminMetrics.inactiveUsers}
          icon={AlertCircle}
          color="red"
          subtitle="need review"
        />
        <MetricCard
          title="Pending Actions"
          value={adminMetrics.pendingActions}
          icon={Clock}
          color="yellow"
          subtitle="require attention"
        />
      </div>

      {/* Role Distribution */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-purple-600" />
          User Role Distribution
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {PERSONA_ROLES.map((role) => {
            const count = users.filter((u) => u.role === role.value).length;
            return (
              <div
                key={role.value}
                className="text-center p-4 bg-gray-50 rounded-lg"
              >
                <p className="text-3xl font-bold text-gray-900">{count}</p>
                <p className="text-sm text-gray-600 mt-1">{role.label}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Activity className="h-5 w-5 text-purple-600" />
          Recent System Activity
        </h3>
        <div className="space-y-3">
          {users.slice(0, 5).map((user) => (
            <div
              key={user.id}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
                  {user.full_name?.charAt(0) || user.username?.charAt(0) || "U"}
                </div>
                <div>
                  <p className="font-medium text-gray-900">
                    {user.full_name || user.username}
                  </p>
                  <p className="text-sm text-gray-500">{user.role}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">
                  {formatDate(user.last_login)}
                </p>
                <p className="text-xs text-gray-400">Last login</p>
              </div>
            </div>
          ))}
          {users.length === 0 && (
            <p className="text-center text-gray-500 py-4">No activity yet</p>
          )}
        </div>
      </div>
    </div>
  );

  // User Management Tab
  const UserManagementTab = () => {
    const filteredUsers = users.filter((user) => {
      if (filters.role !== "all" && user.role !== filters.role) return false;
      if (filters.status !== "all") {
        const isActive = filters.status === "active";
        if (user.is_active !== isActive) return false;
      }
      if (filters.searchTerm) {
        const search = filters.searchTerm.toLowerCase();
        return (
          user.username?.toLowerCase().includes(search) ||
          user.email?.toLowerCase().includes(search) ||
          user.full_name?.toLowerCase().includes(search)
        );
      }
      return true;
    });

    return (
      <div className="space-y-4">
        {/* Actions Bar */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">User Management</h3>
            <button
              onClick={() => setShowUserModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              <UserPlus className="h-4 w-4" />
              Add User
            </button>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search users..."
                value={filters.searchTerm}
                onChange={(e) =>
                  setFilters({ ...filters, searchTerm: e.target.value })
                }
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <select
              value={filters.role}
              onChange={(e) => setFilters({ ...filters, role: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">All Roles</option>
              {PERSONA_ROLES.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
            <select
              value={filters.status}
              onChange={(e) =>
                setFilters({ ...filters, status: e.target.value })
              }
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
              <Download className="h-4 w-4 inline mr-2" />
              Export
            </button>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Role/Persona
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Last Login
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 bg-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
                        {user.full_name?.charAt(0) ||
                          user.username?.charAt(0) ||
                          "U"}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {user.full_name || user.username}
                        </p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeClass(
                        user.role
                      )}`}
                    >
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        user.is_local_user
                          ? "bg-blue-100 text-blue-800"
                          : "bg-green-100 text-green-800"
                      }`}
                    >
                      {user.is_local_user ? "Local" : "Microsoft"}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        user.is_active
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {user.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {formatDate(user.last_login)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {formatDate(user.created_at)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setSelectedUser(user)}
                        className="text-purple-600 hover:text-purple-700"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button className="text-blue-600 hover:text-blue-700">
                        <Edit className="h-4 w-4" />
                      </button>
                      <button className="text-red-600 hover:text-red-700">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr>
                  <td
                    colSpan="7"
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Integrations Tab
  const IntegrationsTab = () => (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          System Integrations
        </h3>
        <div className="space-y-4">
          {integrations.map((integration) => (
            <div
              key={integration.id}
              className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Zap className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">
                    {integration.name}
                  </p>
                  <p className="text-sm text-gray-500">{integration.type}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      integration.status === "active"
                        ? "bg-green-100 text-green-800"
                        : integration.status === "configured"
                        ? "bg-blue-100 text-blue-800"
                        : "bg-yellow-100 text-yellow-800"
                    }`}
                  >
                    {integration.status}
                  </span>
                  {integration.lastSync && (
                    <p className="text-xs text-gray-500 mt-1">
                      Last sync: {formatDate(integration.lastSync)}
                    </p>
                  )}
                </div>
                <button className="px-4 py-2 text-purple-600 hover:text-purple-700">
                  <Settings className="h-5 w-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Configuration Tab
  const ConfigurationTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* System Settings */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Settings className="h-5 w-5 text-purple-600" />
            System Settings
          </h3>
          <div className="space-y-3">
            <button className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center justify-between">
              <span className="text-gray-900">Workflow Stages</span>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </button>
            <button className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center justify-between">
              <span className="text-gray-900">SLA Configuration</span>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </button>
            <button className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center justify-between">
              <span className="text-gray-900">Permission Hierarchies</span>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Security Settings */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Lock className="h-5 w-5 text-purple-600" />
            Security Settings
          </h3>
          <div className="space-y-3">
            <button className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center justify-between">
              <span className="text-gray-900">Microsoft Entra ID Config</span>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </button>
            <button className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center justify-between">
              <span className="text-gray-900">Password Policies</span>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </button>
            <button className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center justify-between">
              <span className="text-gray-900">Session Management</span>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <div className="h-12 w-12 bg-purple-600 rounded-full flex items-center justify-center text-white">
              <Shield className="h-6 w-6" />
            </div>
            Admin Andrew Dashboard
          </h1>
          <p className="text-gray-600 mt-1">
            System administration and configuration management
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            <Filter className="h-4 w-4" />
            Filters
          </button>
          <button
            onClick={fetchAllData}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px overflow-x-auto">
            {[
              { key: "overview", label: "Overview", icon: Activity },
              { key: "users", label: "User Management", icon: Users },
              { key: "team-management", label: "Team Management", icon: Users },
              {
                key: "role-management",
                label: "Role Management",
                icon: Shield,
              },
              {
                key: "flow-management",
                label: "Flow Management",
                icon: TrendingUp,
              },
              { key: "audit-logs", label: "Audit Logs", icon: ScrollText },
              { key: "contacts", label: "Contacts", icon: UserCircle },
              { key: "companies", label: "Companies", icon: Building2 },
              { key: "integrations", label: "Integrations", icon: Zap },
              { key: "configuration", label: "Configuration", icon: Settings },
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-6 py-4 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                    activeTab === tab.key
                      ? "border-purple-600 text-purple-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {loading && activeTab === "overview" ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 text-gray-400 animate-spin" />
            </div>
          ) : (
            <>
              {activeTab === "overview" && <OverviewTab />}
              {activeTab === "users" && <UserManagement />}
              {activeTab === "team-management" && (
                <TeamManagementTab
                  leadRecruiters={leadRecruiters}
                  teams={teams}
                  allRecruiters={allRecruiters}
                  onAddMember={handleAddTeamMember}
                  onRemoveMember={handleRemoveTeamMember}
                  onRefresh={fetchTeamData}
                />
              )}
              {activeTab === "role-management" &&
                (() => {
                  const permissions = rolePermissions[selectedRole] || [];
                  const groupedPermissions = permissions.reduce((acc, perm) => {
                    const category = perm.category || "Other";
                    if (!acc[category]) acc[category] = [];
                    acc[category].push(perm);
                    return acc;
                  }, {});

                  return (
                    <div className="space-y-6">
                      {/* Header with Role Selector */}
                      <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">
                              Role-Based Permissions Management
                            </h2>
                            <p className="text-sm text-gray-600">
                              Manage operations that all users with the selected
                              role can perform
                            </p>
                          </div>
                        </div>

                        {/* Role Selector */}
                        <div className="mt-6 flex items-center gap-4">
                          <label className="text-sm font-medium text-gray-700">
                            Select Role:
                          </label>
                          <div className="flex gap-2 flex-wrap">
                            {[
                              {
                                value: "admin",
                                label: "Admin Andrew",
                                icon: "👨‍💼",
                              },
                              {
                                value: "recruiter",
                                label: "Recruiter Riley",
                                icon: "👤",
                              },
                              { value: "lead", label: "Lead Lena", icon: "👔" },
                              {
                                value: "sales",
                                label: "Sales Sam",
                                icon: "💼",
                              },
                              { value: "hr", label: "HR Hank", icon: "🎓" },
                              {
                                value: "lead_sales_manager",
                                label: "Lead Sales Manager Sarah",
                                icon: "📊",
                              },
                            ].map((role) => (
                              <button
                                key={role.value}
                                onClick={() => setSelectedRole(role.value)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                                  selectedRole === role.value
                                    ? "bg-purple-600 text-white shadow-lg scale-105"
                                    : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                                }`}
                              >
                                <span className="text-lg">{role.icon}</span>
                                <span>{role.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Action Bar */}
                      <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center gap-2">
                          <Shield className="h-5 w-5 text-purple-600" />
                          <h3 className="font-semibold text-gray-900">
                            {permissions.length} Operations
                          </h3>
                        </div>
                        <button
                          onClick={() => {
                            setSelectedPersonaForOperation(selectedRole);
                            setShowAddOperationModal(true);
                          }}
                          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                        >
                          <UserPlus className="h-4 w-4" />
                          Add Operation
                        </button>
                      </div>

                      {/* Stats */}
                      <div className="grid grid-cols-3 gap-4">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <CheckCircle className="h-5 w-5 text-green-600" />
                            <p className="text-sm text-green-600 font-medium">
                              Enabled
                            </p>
                          </div>
                          <p className="text-2xl font-bold text-green-900">
                            {permissions.filter((p) => p.enabled).length}
                          </p>
                        </div>
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <XCircle className="h-5 w-5 text-red-600" />
                            <p className="text-sm text-red-600 font-medium">
                              Disabled
                            </p>
                          </div>
                          <p className="text-2xl font-bold text-red-900">
                            {permissions.filter((p) => !p.enabled).length}
                          </p>
                        </div>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Activity className="h-5 w-5 text-blue-600" />
                            <p className="text-sm text-blue-600 font-medium">
                              Total
                            </p>
                          </div>
                          <p className="text-2xl font-bold text-blue-900">
                            {permissions.length}
                          </p>
                        </div>
                      </div>

                      {/* Permissions by Category */}
                      <div className="space-y-6">
                        {Object.entries(groupedPermissions).map(
                          ([category, perms]) => (
                            <div
                              key={category}
                              className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm"
                            >
                              <div className="bg-gradient-to-r from-purple-50 to-blue-50 px-6 py-3 border-b border-gray-200">
                                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                                  <Lock className="h-4 w-4 text-purple-600" />
                                  {category}
                                </h3>
                              </div>
                              <table className="w-full">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                  <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                      Operation
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                      Description
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-32">
                                      Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-40">
                                      Actions
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                  {perms.map((perm) => (
                                    <tr
                                      key={perm.id}
                                      className="hover:bg-gray-50 transition-colors"
                                    >
                                      <td className="px-6 py-4">
                                        <div className="flex items-center gap-2">
                                          <span className="font-medium text-gray-900">
                                            {perm.name}
                                          </span>
                                          {perm.id.startsWith("custom_") && (
                                            <span className="px-2 py-0.5 bg-purple-100 text-purple-800 text-xs rounded-full font-medium">
                                              Custom
                                            </span>
                                          )}
                                        </div>
                                      </td>
                                      <td className="px-6 py-4 text-sm text-gray-600">
                                        {perm.description}
                                      </td>
                                      <td className="px-6 py-4">
                                        {perm.enabled ? (
                                          <span className="flex items-center gap-1 text-green-600">
                                            <CheckCircle className="h-4 w-4" />
                                            <span className="text-sm font-medium">
                                              Enabled
                                            </span>
                                          </span>
                                        ) : (
                                          <span className="flex items-center gap-1 text-red-600">
                                            <XCircle className="h-4 w-4" />
                                            <span className="text-sm font-medium">
                                              Disabled
                                            </span>
                                          </span>
                                        )}
                                      </td>
                                      <td className="px-6 py-4">
                                        <div className="flex items-center gap-2">
                                          <button
                                            onClick={() =>
                                              togglePermission(
                                                selectedRole,
                                                perm.id
                                              )
                                            }
                                            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                                              perm.enabled
                                                ? "bg-red-100 text-red-700 hover:bg-red-200"
                                                : "bg-green-100 text-green-700 hover:bg-green-200"
                                            }`}
                                          >
                                            {perm.enabled ? (
                                              <span className="flex items-center gap-1">
                                                <Lock className="h-3 w-3" />
                                                Disable
                                              </span>
                                            ) : (
                                              <span className="flex items-center gap-1">
                                                <Unlock className="h-3 w-3" />
                                                Enable
                                              </span>
                                            )}
                                          </button>
                                          {perm.id.startsWith("custom_") && (
                                            <button
                                              onClick={() =>
                                                deleteOperation(
                                                  selectedRole,
                                                  perm.id
                                                )
                                              }
                                              className="p-1.5 bg-gray-100 text-gray-700 hover:bg-red-100 hover:text-red-700 rounded-lg transition-colors"
                                              title="Delete custom operation"
                                            >
                                              <Trash2 className="h-3 w-3" />
                                            </button>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  );
                })()}
              {activeTab === "flow-management" && <FlowManagementTab />}
              {activeTab === "audit-logs" && (
                <div className="space-y-4">
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="mb-4">
                      <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                        <ScrollText className="h-5 w-5 text-purple-600" />
                        System Audit Logs
                      </h2>
                      <p className="text-sm text-gray-600 mt-1">
                        View all system activities, changes, and user actions
                      </p>
                    </div>
                    <AuditLogViewer showFilters={true} />
                  </div>
                </div>
              )}
              {activeTab === "contacts" && (
                <ContactsTab
                  onAddContact={() => setShowContactModal(true)}
                  refreshTrigger={contactsRefreshTrigger}
                />
              )}
              {activeTab === "companies" && (
                <CompaniesTab
                  onAddCompany={() => {
                    setSelectedCompany(null);
                    setShowCompanyModal(true);
                  }}
                  onEditCompany={(company) => {
                    setSelectedCompany(company);
                    setShowCompanyModal(true);
                  }}
                  refreshTrigger={companiesRefreshTrigger}
                />
              )}
              {activeTab === "integrations" && <Integrations />}
              {activeTab === "configuration" && <Configuration />}
            </>
          )}
        </div>
      </div>

      {/* Contact Form Modal */}
      <ContactFormModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        onSuccess={handleContactCreated}
      />

      {/* Company Form Modal */}
      <CompanyCreateForm
        isOpen={showCompanyModal}
        onClose={() => {
          setShowCompanyModal(false);
          setSelectedCompany(null);
        }}
        onSuccess={handleCompanyCreated}
        company={selectedCompany}
      />
    </div>
  );
};

// Flow Management Tab Component
const FlowManagementTab = () => {
  const [activeFlow, setActiveFlow] = useState("candidate"); // 'candidate', 'submission', or 'integrated'

  // Define Candidate Status workflow stages
  const candidateStatusStages = [
    {
      id: "sourced",
      name: "Sourced",
      description: "Candidate identified and profile created",
      icon: "🔍",
      color: "blue",
      category: "initial",
    },
    {
      id: "screened",
      name: "Screened",
      description: "Passed initial screening process",
      icon: "✅",
      color: "green",
      category: "initial",
    },
    {
      id: "rejected",
      name: "Rejected",
      description: "Did not pass screening or interview",
      icon: "❌",
      color: "red",
      category: "terminal",
    },
    {
      id: "available",
      name: "Available",
      description: "Passed screening and ready for submissions",
      icon: "🟢",
      color: "green",
      category: "ready",
    },
    {
      id: "offer_made",
      name: "Offer Made",
      description: "Job offer extended to candidate",
      icon: "💼",
      color: "purple",
      category: "offer",
    },
    {
      id: "offer_accepted",
      name: "Offer Accepted",
      description: "Candidate accepted the job offer",
      icon: "🎉",
      color: "green",
      category: "offer",
    },
    {
      id: "onboarding_in_progress",
      name: "Onboarding in Progress",
      description: "Candidate onboarding process started",
      icon: "📋",
      color: "blue",
      category: "onboarding",
    },
    {
      id: "onboarded",
      name: "Onboarded",
      description: "Onboarding process completed",
      icon: "✓",
      color: "green",
      category: "onboarding",
    },
    {
      id: "started",
      name: "Started",
      description: "Candidate started working",
      icon: "🚀",
      color: "green",
      category: "active",
    },
    {
      id: "unavailable",
      name: "Unavailable",
      description: "Candidate no longer available",
      icon: "🔒",
      color: "gray",
      category: "terminal",
    },
  ];

  const candidateCategories = {
    initial: { name: "Initial Phase", color: "blue", icon: "🌱" },
    ready: { name: "Ready for Submission", color: "green", icon: "✨" },
    offer: { name: "Offer Phase", color: "purple", icon: "💼" },
    onboarding: { name: "Onboarding", color: "indigo", icon: "📚" },
    active: { name: "Active Employment", color: "green", icon: "🚀" },
    terminal: { name: "Terminal States", color: "red", icon: "⏹️" },
  };

  // Define Client Submission workflow stages
  const submissionStatusStages = [
    {
      id: "submitted",
      name: "Submitted",
      description: "Candidate profile submitted to client for review",
      icon: "📤",
      color: "blue",
      category: "submission",
    },
    {
      id: "rejected_pre_interview",
      name: "Rejected (Pre-Interview)",
      description: "Submission rejected by client before interview",
      icon: "❌",
      color: "red",
      category: "terminal",
    },
    {
      id: "interview_scheduled",
      name: "Interview Scheduled",
      description: "Interview scheduled with client",
      icon: "📅",
      color: "purple",
      category: "interview",
    },
    {
      id: "interviewed",
      name: "Interviewed",
      description: "Interview completed, awaiting feedback",
      icon: "✅",
      color: "indigo",
      category: "interview",
    },
    {
      id: "rejected_post_interview",
      name: "Rejected (Post-Interview)",
      description: "Submission rejected by client after interview",
      icon: "❌",
      color: "red",
      category: "terminal",
    },
    {
      id: "offer_made",
      name: "Offer Made",
      description: "Job offer extended to candidate (syncs candidate status)",
      icon: "💼",
      color: "green",
      category: "offer",
    },
    {
      id: "offer_accepted",
      name: "Offer Accepted",
      description: "Candidate accepted the job offer (syncs candidate status)",
      icon: "🎉",
      color: "green",
      category: "offer",
    },
    {
      id: "offer_rejected",
      name: "Offer Rejected",
      description: "Candidate rejected offer (candidate returns to Available)",
      icon: "💔",
      color: "orange",
      category: "terminal",
    },
    {
      id: "onboarding",
      name: "Onboarding",
      description: "Onboarding process in progress (syncs candidate status)",
      icon: "📋",
      color: "indigo",
      category: "onboarding",
    },
    {
      id: "onboarded",
      name: "Onboarded",
      description:
        "Onboarding completed - final success state (syncs candidate status)",
      icon: "✓",
      color: "indigo",
      category: "onboarding",
    },
    {
      id: "started",
      name: "Started",
      description:
        "Candidate started working at client (syncs candidate status)",
      icon: "🚀",
      color: "teal",
      category: "success",
    },
  ];

  const getColorClasses = (color) => {
    const colorMap = {
      blue: {
        bg: "bg-blue-50",
        border: "border-blue-200",
        text: "text-blue-700",
        iconBg: "bg-blue-100",
      },
      green: {
        bg: "bg-green-50",
        border: "border-green-200",
        text: "text-green-700",
        iconBg: "bg-green-100",
      },
      yellow: {
        bg: "bg-yellow-50",
        border: "border-yellow-200",
        text: "text-yellow-700",
        iconBg: "bg-yellow-100",
      },
      orange: {
        bg: "bg-orange-50",
        border: "border-orange-200",
        text: "text-orange-700",
        iconBg: "bg-orange-100",
      },
      red: {
        bg: "bg-red-50",
        border: "border-red-200",
        text: "text-red-700",
        iconBg: "bg-red-100",
      },
      purple: {
        bg: "bg-purple-50",
        border: "border-purple-200",
        text: "text-purple-700",
        iconBg: "bg-purple-100",
      },
      indigo: {
        bg: "bg-indigo-50",
        border: "border-indigo-200",
        text: "text-indigo-700",
        iconBg: "bg-indigo-100",
      },
      teal: {
        bg: "bg-teal-50",
        border: "border-teal-200",
        text: "text-teal-700",
        iconBg: "bg-teal-100",
      },
      pink: {
        bg: "bg-pink-50",
        border: "border-pink-200",
        text: "text-pink-700",
        iconBg: "bg-pink-100",
      },
      gray: {
        bg: "bg-gray-50",
        border: "border-gray-200",
        text: "text-gray-700",
        iconBg: "bg-gray-100",
      },
    };
    return colorMap[color] || colorMap.blue;
  };

  const workflowStages =
    activeFlow === "candidate" ? candidateStatusStages : submissionStatusStages;
  const stageCategories =
    activeFlow === "candidate"
      ? candidateCategories
      : {
          submission: { name: "Submission Phase", color: "blue", icon: "📤" },
          interview: { name: "Interview Process", color: "purple", icon: "💬" },
          offer: { name: "Offer Management", color: "green", icon: "✨" },
          onboarding: { name: "Onboarding", color: "indigo", icon: "📚" },
          success: { name: "Successfully Placed", color: "teal", icon: "🎯" },
          terminal: { name: "Terminal States", color: "red", icon: "⏹️" },
        };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Flow Management
        </h2>
        <p className="text-gray-600">
          Visual representation of candidate and submission workflows from
          initial sourcing through completion.
        </p>
      </div>

      {/* Flow Type Selector */}
      <div className="flex gap-4 bg-gray-100 p-2 rounded-lg w-fit">
        <button
          onClick={() => setActiveFlow("candidate")}
          className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
            activeFlow === "candidate"
              ? "bg-white text-purple-600 shadow-md"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          <Users className="h-5 w-5" />
          Candidate Status Flow
          <span
            className={`px-2 py-0.5 rounded-full text-xs ${
              activeFlow === "candidate"
                ? "bg-purple-100 text-purple-700"
                : "bg-gray-200 text-gray-600"
            }`}
          >
            {candidateStatusStages.length} stages
          </span>
        </button>
        <button
          onClick={() => setActiveFlow("submission")}
          className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
            activeFlow === "submission"
              ? "bg-white text-blue-600 shadow-md"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          <Target className="h-5 w-5" />
          Client Submission Flow
          <span
            className={`px-2 py-0.5 rounded-full text-xs ${
              activeFlow === "submission"
                ? "bg-blue-100 text-blue-700"
                : "bg-gray-200 text-gray-600"
            }`}
          >
            {submissionStatusStages.length} stages
          </span>
        </button>
        <button
          onClick={() => setActiveFlow("journey")}
          className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
            activeFlow === "journey"
              ? "bg-white text-orange-600 shadow-md"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          <TrendingUp className="h-5 w-5" />
          Journey View
          <span
            className={`px-2 py-0.5 rounded-full text-xs ${
              activeFlow === "journey"
                ? "bg-orange-100 text-orange-700"
                : "bg-gray-200 text-gray-600"
            }`}
          >
            Vertical
          </span>
        </button>
      </div>

      {/* Journey View */}
      {activeFlow === "journey" && <CandidateJourneyFlow />}

      {/* Flow Description (for candidate/submission views only) */}
      {(activeFlow === "candidate" || activeFlow === "submission") && (
        <>
          <div
            className={`p-4 rounded-lg border-l-4 ${
              activeFlow === "candidate"
                ? "bg-purple-50 border-purple-500"
                : "bg-blue-50 border-blue-500"
            }`}
          >
            <h3
              className={`font-semibold mb-1 ${
                activeFlow === "candidate" ? "text-purple-900" : "text-blue-900"
              }`}
            >
              {activeFlow === "candidate"
                ? "Candidate Status Flow"
                : "Client Submission Flow"}
            </h3>
            <p
              className={`text-sm ${
                activeFlow === "candidate" ? "text-purple-700" : "text-blue-700"
              }`}
            >
              {activeFlow === "candidate"
                ? "Tracks the overall lifecycle of a candidate from initial sourcing through employment. This status applies to the candidate record itself and indicates their current availability and employment state."
                : "Tracks individual job submissions for a candidate. A single candidate can have multiple submissions (different jobs/clients), each with its own submission status tracking the progress through the hiring pipeline."}
            </p>
          </div>
        </>
      )}

      {/* Status Synchronization Info (for candidate/submission views only) */}
      {activeFlow !== "integrated" && (
        <>
          <div className="bg-gradient-to-r from-green-50 to-teal-50 border border-green-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-green-600" />
              Status Synchronization Rules
            </h3>
            <p className="text-sm text-gray-700 mb-4">
              Candidate status and submission status are automatically
              synchronized based on specific workflow events:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg p-4 border border-green-200">
                <h4 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                  <ChevronRight className="h-4 w-4" />
                  Submission → Candidate Sync
                </h4>
                <ul className="text-sm space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 font-bold">•</span>
                    <span>
                      <strong>Offer Made:</strong> Candidate status → Offer Made
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 font-bold">•</span>
                    <span>
                      <strong>Offer Accepted:</strong> Candidate status → Offer
                      Accepted
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 font-bold">•</span>
                    <span>
                      <strong>Offer Rejected:</strong> Candidate status →
                      Available (back to pool)
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 font-bold">•</span>
                    <span>
                      <strong>Onboarding/Onboarded/Started:</strong> Syncs
                      candidate status
                    </span>
                  </li>
                </ul>
              </div>
              <div className="bg-white rounded-lg p-4 border border-purple-200">
                <h4 className="font-semibold text-purple-900 mb-2 flex items-center gap-2">
                  <ChevronRight className="h-4 w-4" />
                  Workflow Flow
                </h4>
                <ul className="text-sm space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 font-bold">1.</span>
                    <span>
                      Candidate: Sourced → Screened → Available/Rejected
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 font-bold">2.</span>
                    <span>If Available: Can be submitted to jobs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 font-bold">3.</span>
                    <span>
                      Submission: Submitted → Interview → Offer stages
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 font-bold">4.</span>
                    <span>
                      Offer stages automatically sync candidate status
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Flow Categories (for candidate/submission views only) */}
      {activeFlow !== "integrated" && (
        <>
          {Object.entries(stageCategories).map(([categoryKey, category]) => {
            const stages = workflowStages.filter(
              (stage) => stage.category === categoryKey
            );

            if (stages.length === 0) return null;

            return (
              <div
                key={categoryKey}
                className="bg-white border border-gray-200 rounded-lg p-6"
              >
                {/* Category Header */}
                <div className="flex items-center gap-3 mb-6">
                  <span className="text-3xl">{category.icon}</span>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">
                      {category.name}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {stages.length} stages in this phase
                    </p>
                  </div>
                </div>

                {/* Stages in this category */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {stages.map((stage, index) => {
                    const colors = getColorClasses(stage.color);

                    return (
                      <div
                        key={stage.id}
                        className={`relative ${colors.bg} border ${colors.border} rounded-lg p-4 transition-all hover:shadow-md`}
                      >
                        {/* Stage Icon */}
                        <div
                          className={`${colors.iconBg} w-12 h-12 rounded-full flex items-center justify-center mb-3`}
                        >
                          <span className="text-2xl">{stage.icon}</span>
                        </div>

                        {/* Stage Name */}
                        <h4 className={`font-semibold ${colors.text} mb-2`}>
                          {stage.name}
                        </h4>

                        {/* Stage Description */}
                        <p className="text-sm text-gray-600">
                          {stage.description}
                        </p>

                        {/* Stage ID Badge */}
                        <div className="mt-3">
                          <span
                            className={`inline-block px-2 py-1 ${colors.iconBg} ${colors.text} text-xs font-mono rounded`}
                          >
                            {stage.id}
                          </span>
                        </div>

                        {/* Arrow indicator (except last in category and not terminal states) */}
                        {index < stages.length - 1 &&
                          categoryKey !== "rejection" &&
                          categoryKey !== "terminal" && (
                            <div className="absolute -right-2 top-1/2 transform -translate-y-1/2 hidden lg:block">
                              <ChevronRight
                                className={`h-6 w-6 ${colors.text}`}
                              />
                            </div>
                          )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Workflow Summary */}
          <div
            className={`bg-gradient-to-r ${
              activeFlow === "candidate"
                ? "from-purple-50 to-indigo-50 border-purple-200"
                : "from-blue-50 to-cyan-50 border-blue-200"
            } border rounded-lg p-6`}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Activity
                className={`h-5 w-5 ${
                  activeFlow === "candidate"
                    ? "text-purple-600"
                    : "text-blue-600"
                }`}
              />
              Workflow Summary
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 text-center">
              {Object.entries(stageCategories).map(([key, category]) => {
                const count = workflowStages.filter(
                  (s) => s.category === key
                ).length;
                if (count === 0) return null;
                return (
                  <div key={key} className="bg-white rounded-lg p-4 shadow-sm">
                    <span className="text-3xl mb-2 block">{category.icon}</span>
                    <p className="text-2xl font-bold text-gray-900">{count}</p>
                    <p className="text-xs text-gray-600 mt-1">
                      {category.name}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Total Stages Info */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Total{" "}
                  {activeFlow === "candidate"
                    ? "Candidate Status"
                    : "Submission"}{" "}
                  Stages
                </h3>
                <p className="text-gray-600 mt-1">
                  Complete{" "}
                  {activeFlow === "candidate"
                    ? "candidate lifecycle"
                    : "submission workflow"}{" "}
                  encompasses {workflowStages.length} distinct stages
                </p>
              </div>
              <div className="text-right">
                <p
                  className={`text-4xl font-bold ${
                    activeFlow === "candidate"
                      ? "text-purple-600"
                      : "text-blue-600"
                  }`}
                >
                  {workflowStages.length}
                </p>
                <p className="text-sm text-gray-600 mt-1">Stages</p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Metric Card Component
const MetricCard = ({ title, value, icon: Icon, color, subtitle }) => {
  const colorClasses = {
    blue: "from-blue-500 to-blue-600",
    green: "from-green-500 to-green-600",
    orange: "from-orange-500 to-orange-600",
    purple: "from-purple-500 to-purple-600",
    teal: "from-teal-500 to-teal-600",
    red: "from-red-500 to-red-600",
    yellow: "from-yellow-500 to-yellow-600",
  };

  // Role Management Tab Component (combines all role permissions)
  const RoleManagementTab = () => {
    const roleOptions = [
      { value: "admin", label: "Admin Andrew", color: "red", icon: "👨‍💼" },
      {
        value: "recruiter",
        label: "Recruiter Riley",
        color: "green",
        icon: "👤",
      },
      { value: "lead", label: "Lead Lena", color: "blue", icon: "👔" },
      { value: "sales", label: "Sales Sam", color: "orange", icon: "💼" },
      { value: "hr", label: "HR Hank", color: "indigo", icon: "🎓" },
    ];

    const currentRoleOption = roleOptions.find((r) => r.value === selectedRole);
    const permissions = rolePermissions[selectedRole] || [];

    // Group by category
    const groupedPermissions = permissions.reduce((acc, perm) => {
      const category = perm.category || "Other";
      if (!acc[category]) acc[category] = [];
      acc[category].push(perm);
      return acc;
    }, {});

    return (
      <div className="space-y-6">
        {/* Header with Role Selector */}
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Role-Based Permissions Management
              </h2>
              <p className="text-sm text-gray-600">
                Manage operations that all users with the selected role can
                perform
              </p>
            </div>
          </div>

          {/* Role Selector */}
          <div className="mt-6 flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700">
              Select Role:
            </label>
            <div className="flex gap-2">
              {roleOptions.map((role) => (
                <button
                  key={role.value}
                  onClick={() => setSelectedRole(role.value)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                    selectedRole === role.value
                      ? `bg-${role.color}-600 text-white shadow-lg scale-105`
                      : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <span className="text-lg">{role.icon}</span>
                  <span>{role.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Action Bar */}
        <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-purple-600" />
            <h3 className="font-semibold text-gray-900">
              {currentRoleOption?.label} Permissions
            </h3>
          </div>
          <button
            onClick={() => {
              setSelectedPersonaForOperation(selectedRole);
              setShowAddOperationModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <UserPlus className="h-4 w-4" />
            Add Operation
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <p className="text-sm text-green-600 font-medium">
                Enabled Operations
              </p>
            </div>
            <p className="text-2xl font-bold text-green-900">
              {permissions.filter((p) => p.enabled).length}
            </p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <XCircle className="h-5 w-5 text-red-600" />
              <p className="text-sm text-red-600 font-medium">
                Disabled Operations
              </p>
            </div>
            <p className="text-2xl font-bold text-red-900">
              {permissions.filter((p) => !p.enabled).length}
            </p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="h-5 w-5 text-blue-600" />
              <p className="text-sm text-blue-600 font-medium">
                Total Operations
              </p>
            </div>
            <p className="text-2xl font-bold text-blue-900">
              {permissions.length}
            </p>
          </div>
        </div>

        {/* Permissions by Category */}
        <div className="space-y-6">
          {Object.entries(groupedPermissions).map(([category, perms]) => (
            <div
              key={category}
              className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm"
            >
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 px-6 py-3 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Lock className="h-4 w-4 text-purple-600" />
                  {category}
                </h3>
              </div>
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Operation
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-32">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-40">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {perms.map((perm) => (
                    <tr
                      key={perm.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">
                            {perm.name}
                          </span>
                          {perm.id.startsWith("custom_") && (
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-800 text-xs rounded-full font-medium">
                              Custom
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {perm.description}
                      </td>
                      <td className="px-6 py-4">
                        {perm.enabled ? (
                          <span className="flex items-center gap-1 text-green-600">
                            <CheckCircle className="h-4 w-4" />
                            <span className="text-sm font-medium">Enabled</span>
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-red-600">
                            <XCircle className="h-4 w-4" />
                            <span className="text-sm font-medium">
                              Disabled
                            </span>
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() =>
                              togglePermission(selectedRole, perm.id)
                            }
                            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                              perm.enabled
                                ? "bg-red-100 text-red-700 hover:bg-red-200"
                                : "bg-green-100 text-green-700 hover:bg-green-200"
                            }`}
                          >
                            {perm.enabled ? (
                              <span className="flex items-center gap-1">
                                <Lock className="h-3 w-3" />
                                Disable
                              </span>
                            ) : (
                              <span className="flex items-center gap-1">
                                <Unlock className="h-3 w-3" />
                                Enable
                              </span>
                            )}
                          </button>
                          {perm.id.startsWith("custom_") && (
                            <button
                              onClick={() =>
                                deleteOperation(selectedRole, perm.id)
                              }
                              className="p-1.5 bg-gray-100 text-gray-700 hover:bg-red-100 hover:text-red-700 rounded-lg transition-colors"
                              title="Delete custom operation"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div
      className={`bg-gradient-to-br ${colorClasses[color]} rounded-lg p-6 text-white`}
    >
      <div className="flex items-center justify-between mb-2">
        <p className="text-white text-opacity-90 text-sm font-medium">
          {title}
        </p>
        <Icon className="h-8 w-8 text-white text-opacity-80" />
      </div>
      <p className="text-3xl font-bold">{value}</p>
      {subtitle && (
        <p className="text-white text-opacity-75 text-xs mt-1">{subtitle}</p>
      )}
    </div>
  );
};

// Team Management Tab Component
const TeamManagementTab = ({
  leadRecruiters,
  teams,
  allRecruiters,
  onAddMember,
  onRemoveMember,
  onRefresh,
}) => {
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);

  // Get team for selected lead
  const selectedTeam = teams.find((t) => t.team_lead_id === selectedLeadId);

  // Get available recruiters (not in selected team)
  const availableRecruiters = allRecruiters.filter(
    (r) => !selectedTeam?.team_members?.includes(r.id)
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Team Management
        </h2>
        <p className="text-sm text-gray-600">
          Manage recruiting teams and assign team members to lead recruiters
        </p>
      </div>

      {/* Lead Recruiters List */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-900">
            Lead Recruiters
          </h3>
        </div>

        {leadRecruiters.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <Users className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p>No lead recruiters found</p>
            <p className="text-sm mt-2">
              Create users with "lead" role to get started
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {leadRecruiters.map((lead) => {
              const team = teams.find((t) => t.team_lead_id === lead.id);
              const isExpanded = selectedLeadId === lead.id;

              return (
                <div key={lead.id} className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                        <Users className="h-6 w-6 text-purple-600" />
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">
                          {lead.full_name}
                        </h4>
                        <p className="text-sm text-gray-600">{lead.email}</p>
                        {team && (
                          <p className="text-xs text-purple-600 mt-1">
                            Team: {team.team_name} •{" "}
                            {team.team_members?.length || 0} members
                          </p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() =>
                        setSelectedLeadId(isExpanded ? null : lead.id)
                      }
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      {isExpanded ? "Hide Team" : "Manage Team"}
                    </button>
                  </div>

                  {/* Expanded Team Management */}
                  {isExpanded && (
                    <div className="mt-6 pl-16">
                      {!team ? (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                          <AlertCircle className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
                          <p className="text-sm text-yellow-800">
                            No team created for this lead yet
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <h5 className="font-medium text-gray-900">
                              Team Members ({team.team_members?.length || 0})
                            </h5>
                            <button
                              onClick={() => setShowAddMemberModal(true)}
                              className="px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 flex items-center gap-2"
                            >
                              <Plus className="h-4 w-4" />
                              Add Member
                            </button>
                          </div>

                          {team.team_members?.length === 0 ? (
                            <div className="bg-gray-50 rounded-lg p-4 text-center text-gray-500 text-sm">
                              No team members yet. Click "Add Member" to get
                              started.
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {team.team_members.map((memberId) => {
                                const member = allRecruiters.find(
                                  (r) => r.id === memberId
                                );
                                if (!member) return null;

                                return (
                                  <div
                                    key={memberId}
                                    className="flex items-center justify-between bg-gray-50 rounded-lg p-3"
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                        <UserCircle className="h-5 w-5 text-blue-600" />
                                      </div>
                                      <div>
                                        <p className="font-medium text-gray-900">
                                          {member.full_name}
                                        </p>
                                        <p className="text-xs text-gray-600">
                                          {member.email}
                                        </p>
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => {
                                        if (
                                          window.confirm(
                                            `Remove ${member.full_name} from team?`
                                          )
                                        ) {
                                          onRemoveMember(team.id, memberId);
                                        }
                                      }}
                                      className="px-3 py-1 bg-red-100 text-red-700 text-sm rounded-lg hover:bg-red-200 transition-colors"
                                    >
                                      Remove
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Add Member Modal */}
                          {showAddMemberModal && (
                            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                              <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                                <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 flex justify-between items-center">
                                  <h3 className="text-xl font-bold text-white">
                                    Add Team Member
                                  </h3>
                                  <button
                                    onClick={() => setShowAddMemberModal(false)}
                                    className="text-white hover:text-gray-200"
                                  >
                                    <X className="h-6 w-6" />
                                  </button>
                                </div>

                                <div className="p-6">
                                  {availableRecruiters.length === 0 ? (
                                    <div className="text-center text-gray-500 py-8">
                                      <p>No available recruiters to add</p>
                                      <p className="text-sm mt-2">
                                        All recruiters are already in this team
                                      </p>
                                    </div>
                                  ) : (
                                    <div className="space-y-2">
                                      <label className="block text-sm font-medium text-gray-700 mb-3">
                                        Select Recruiter to Add:
                                      </label>
                                      {availableRecruiters.map((recruiter) => (
                                        <button
                                          key={recruiter.id}
                                          onClick={() => {
                                            onAddMember(team.id, recruiter.id);
                                            setShowAddMemberModal(false);
                                          }}
                                          className="w-full flex items-center gap-3 p-3 rounded-lg border-2 border-gray-200 hover:border-green-400 hover:bg-green-50 transition-all"
                                        >
                                          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                            <UserCircle className="h-6 w-6 text-green-600" />
                                          </div>
                                          <div className="flex-1 text-left">
                                            <p className="font-medium text-gray-900">
                                              {recruiter.full_name}
                                            </p>
                                            <p className="text-sm text-gray-600">
                                              {recruiter.email}
                                            </p>
                                          </div>
                                          <Plus className="h-5 w-5 text-green-600" />
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>

                                <div className="bg-gray-50 px-6 py-4 flex justify-end">
                                  <button
                                    onClick={() => setShowAddMemberModal(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAndrewDashboard;
